import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Plus, Eye, EyeOff, KeyRound, Pencil, FlaskConical } from "lucide-react";

interface AIProvider {
  id: string;
  provider_name: string;
  api_key_prefix: string | null;
  base_url: string;
  models: string[];
  active: boolean;
  priority: number;
  last_tested_at: string | null;
  last_test_ok: boolean | null;
  created_at: string;
  org_id: string;
  endpoint_url: string | null;
  request_format: string | null;
}

function useOrgId() {
  return useQuery({
    queryKey: ["my-org-id"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data } = await supabase.from("org_members").select("org_id").eq("user_id", user.id).limit(1).maybeSingle();
      return data?.org_id ?? null;
    },
    staleTime: Infinity,
  });
}

const KNOWN_PROVIDERS = [
  { name: "OpenAI", base_url: "https://api.openai.com/v1", endpoint_url: "https://api.openai.com/v1/chat/completions", format: "openai", models: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "o1", "o1-mini", "o3-mini"] },
  { name: "Anthropic", base_url: "https://api.anthropic.com/v1", endpoint_url: "https://api.anthropic.com/v1/messages", format: "anthropic", models: ["claude-sonnet-4-5-20250929", "claude-3-5-haiku-20241022", "claude-3-opus-20240229"] },
  { name: "Google AI", base_url: "https://generativelanguage.googleapis.com/v1beta", endpoint_url: "https://generativelanguage.googleapis.com/v1beta/openai/chat/completions", format: "gemini", models: ["gemini-2.5-pro", "gemini-3-flash-preview", "gemini-2.0-flash"] },
  { name: "Mistral", base_url: "https://api.mistral.ai/v1", endpoint_url: "https://api.mistral.ai/v1/chat/completions", format: "openai", models: ["mistral-large-latest", "mistral-medium-latest", "mistral-small-latest"] },
  { name: "Personalizado", base_url: "", endpoint_url: "", format: "openai", models: [] },
];

const REQUEST_FORMATS = [
  { value: "openai", label: "OpenAI Compatible" },
  { value: "anthropic", label: "Anthropic" },
  { value: "gemini", label: "Google Gemini" },
  { value: "custom", label: "Custom" },
];

export default function AIProviders() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: orgId } = useOrgId();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [form, setForm] = useState({
    provider_name: "", api_key: "", base_url: "", models: "" as string,
    active: true, priority: 10, endpoint_url: "", request_format: "openai",
  });

  const { data: providers = [], isLoading } = useQuery({
    queryKey: ["ai-providers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_ai_providers_safe")
        .select("id, provider_name, api_key_prefix, base_url, models, active, priority, last_tested_at, last_test_ok, created_at, org_id, endpoint_url, request_format")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as AIProvider[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof form & { id?: string }) => {
      const modelsArray = payload.models.split(",").map((m) => m.trim()).filter(Boolean);
      const baseRecord = {
        provider_name: payload.provider_name,
        base_url: payload.base_url,
        models: modelsArray,
        active: payload.active,
        priority: payload.priority,
        endpoint_url: payload.endpoint_url || null,
        request_format: payload.request_format || "openai",
      };
      if (payload.id) {
        const body: Record<string, unknown> = { id: payload.id, ...baseRecord };
        if (payload.api_key.trim()) body.api_key = payload.api_key.trim();
        const { error } = await supabase.functions.invoke("provider-upsert", { body });
        if (error) throw error;
      } else {
        if (!orgId) throw new Error("Organização não encontrada");
        const { error } = await supabase.functions.invoke("provider-upsert", {
          body: { ...baseRecord, api_key: payload.api_key, org_id: orgId },
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-providers"] });
      toast({ title: editingId ? "Provedor atualizado" : "Provedor adicionado" });
      resetForm();
    },
    onError: (e) => toast({ title: "Erro", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("syra_ai_providers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ai-providers"] });
      toast({ title: "Provedor removido" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("syra_ai_providers").update({ active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ai-providers"] }),
  });

  function resetForm() {
    setForm({ provider_name: "", api_key: "", base_url: "", models: "", active: true, priority: 10, endpoint_url: "", request_format: "openai" });
    setEditingId(null);
    setDialogOpen(false);
  }

  function openEdit(p: AIProvider) {
    setForm({
      provider_name: p.provider_name, api_key: "", base_url: p.base_url,
      models: p.models.join(", "), active: p.active, priority: p.priority,
      endpoint_url: p.endpoint_url || "", request_format: p.request_format || "openai",
    });
    setEditingId(p.id);
    setDialogOpen(true);
  }

  const [testingId, setTestingId] = useState<string | null>(null);

  async function testProvider(p: AIProvider) {
    setTestingId(p.id);
    try {
      const { data, error } = await supabase.functions.invoke("test-ai-provider", {
        body: { provider_id: p.id },
      });
      if (error) throw error;
      const result = data as { ok: boolean; message: string; latency_ms?: number };
      toast({
        title: result.ok ? "Teste OK" : "Teste falhou",
        description: result.message,
        variant: result.ok ? "default" : "destructive",
      });
      queryClient.invalidateQueries({ queryKey: ["ai-providers"] });
    } catch (e: any) {
      toast({ title: "Erro no teste", description: e.message, variant: "destructive" });
    } finally {
      setTestingId(null);
    }
  }

  function selectKnownProvider(name: string) {
    const known = KNOWN_PROVIDERS.find((p) => p.name === name);
    if (known) {
      setForm((f) => ({
        ...f, provider_name: known.name, base_url: known.base_url,
        models: known.models.join(", "), endpoint_url: known.endpoint_url,
        request_format: known.format,
      }));
    }
  }

  function maskKey(keyPrefix?: string | null) {
    if (!keyPrefix) return "••••••••";
    return `${keyPrefix}••••••••`;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <KeyRound className="w-6 h-6" /> Provedores de IA
          </h1>
          <p className="text-muted-foreground">Gerencie chaves de API para modelos avançados de IA</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) resetForm(); setDialogOpen(o); }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> Novo Provedor</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Editar Provedor" : "Adicionar Provedor de IA"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!editingId && (
                <div>
                  <Label>Provedor Rápido</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {KNOWN_PROVIDERS.map((p) => (
                      <Button key={p.name} type="button" variant={form.provider_name === p.name ? "default" : "outline"} size="sm" onClick={() => selectKnownProvider(p.name)}>
                        {p.name}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Nome do Provedor</Label>
                <Input value={form.provider_name} onChange={(e) => setForm((f) => ({ ...f, provider_name: e.target.value }))} placeholder="Ex: OpenAI" />
              </div>
              <div>
                <Label>Chave de API</Label>
                <Input type="password" value={form.api_key} onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))} placeholder="sk-..." />
              </div>
              <div>
                <Label>URL Base da API</Label>
                <Input value={form.base_url} onChange={(e) => setForm((f) => ({ ...f, base_url: e.target.value }))} placeholder="https://api.openai.com/v1" />
              </div>
              <div>
                <Label>Endpoint URL (opcional, sobrescreve a URL Base)</Label>
                <Input value={form.endpoint_url} onChange={(e) => setForm((f) => ({ ...f, endpoint_url: e.target.value }))} placeholder="https://api.openai.com/v1/chat/completions" />
              </div>
              <div>
                <Label>Formato de Requisição</Label>
                <Select value={form.request_format} onValueChange={(v) => setForm((f) => ({ ...f, request_format: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REQUEST_FORMATS.map((rf) => (
                      <SelectItem key={rf.value} value={rf.value}>{rf.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Modelos Disponíveis (separados por vírgula)</Label>
                <Input value={form.models} onChange={(e) => setForm((f) => ({ ...f, models: e.target.value }))} placeholder="gpt-4o, gpt-4o-mini" />
              </div>
              <Button className="w-full" disabled={!form.provider_name || (!editingId && !form.api_key) || saveMutation.isPending} onClick={() => saveMutation.mutate({ ...form, id: editingId ?? undefined })}>
                {saveMutation.isPending ? "Salvando..." : editingId ? "Atualizar" : "Adicionar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Carregando...</p>
      ) : providers.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <KeyRound className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhum provedor configurado</h3>
            <p className="text-muted-foreground mb-4">Adicione chaves de API de provedores como OpenAI, Anthropic ou Google para usar modelos avançados.</p>
            <Button onClick={() => setDialogOpen(true)}><Plus className="w-4 h-4 mr-2" /> Adicionar Provedor</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {providers.map((p) => (
            <Card key={p.id} className={`hover:shadow-lg hover:scale-[1.02] hover:-translate-y-0.5 transition-all duration-300 ease-out ${!p.active ? "opacity-60" : ""}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{p.provider_name}</CardTitle>
                  <Switch checked={p.active} onCheckedChange={(active) => toggleMutation.mutate({ id: p.id, active })} />
                </div>
                <CardDescription className="text-xs truncate">
                  {p.endpoint_url || p.base_url || "URL não configurada"} · {p.request_format || "openai"} · P{p.priority}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">API Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                      {showKeys[p.id] ? "Chave protegida no backend" : maskKey(p.api_key_prefix)}
                    </code>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowKeys((s) => ({ ...s, [p.id]: !s[p.id] }))}>
                      {showKeys[p.id] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Modelos</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {p.models.length > 0 ? p.models.map((m) => <Badge key={m} variant="secondary" className="text-xs">{m}</Badge>) : <span className="text-xs text-muted-foreground">Nenhum modelo</span>}
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(p)}>
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => testProvider(p)} disabled={testingId === p.id} title="Testar provedor">
                    <FlaskConical className={`w-3 h-3 ${testingId === p.id ? "animate-spin" : ""}`} />
                  </Button>
                  <ConfirmDeleteDialog itemName={p.provider_name} itemType="provedor" onConfirm={() => deleteMutation.mutate(p.id)} triggerVariant="destructive" triggerSize="sm" />
                </div>
                {p.last_tested_at && (
                  <p className="text-xs text-muted-foreground pt-1">
                    Último teste: {new Date(p.last_tested_at).toLocaleString("pt-BR")}: {p.last_test_ok ? "✅ OK" : "❌ Falhou"}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
